import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import SplashScreen from './components/SplashScreen';
import Onboarding from './components/Onboarding';
import PrototypeNav from './components/PrototypeNav';
import PrototypeNavWrapper from './components/PrototypeNavWrapper';
import Home from './pages/Home';
import Categories from './pages/Categories';
import Calendar from './pages/Calendar';
import Store from './pages/Store';
import Login from './pages/Login';
import OTP from './pages/OTP';
import Profile from './pages/Profile';
import MyCourses from './pages/MyCourses';
import Quizzes from './pages/Quizzes';
import MyBooks from './pages/MyBooks';
import About from './pages/About';
import Privacy from './pages/Privacy';
import EditProfile from './pages/EditProfile';
import CourseDetails from './pages/CourseDetails';
import ChaptersList from './pages/ChaptersList';
import ChapterDetails from './pages/ChapterDetails';
import VideoPlayer from './pages/VideoPlayer';
import QuizAttempt from './pages/QuizAttempt';
import QuizResults from './pages/QuizResults';
import LiveSessionsList from './pages/LiveSessionsList';
import LiveSessionDetails from './pages/LiveSessionDetails';
import LiveSessionRoom from './pages/LiveSessionRoom';
import OnlineCourseDetails from './pages/OnlineCourseDetails';
import OnlineChapterDetails from './pages/OnlineChapterDetails';
import PaymentProof from './pages/PaymentProof';
import PaymentStatus from './pages/PaymentStatus';
import ForgotPassword from './pages/ForgotPassword';
import DeleteAccount from './pages/DeleteAccount';
import Support from './pages/Support';
import AllCourses from './pages/AllCourses';
import AllResources from './pages/AllResources';
import AllLiveSessions from './pages/AllLiveSessions';
import ResourceDetails from './pages/ResourceDetails';
import './App.css';

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState(
    localStorage.getItem('hasSeenOnboarding') === 'true'
  );
  const [currentScreen, setCurrentScreen] = useState('splash');

  useEffect(() => {
    // Determine current screen for prototype nav
    if (showSplash) {
      setCurrentScreen('splash');
    } else if (!hasSeenOnboarding) {
      setCurrentScreen('onboarding');
    } else {
      const path = window.location.pathname;
      if (path === '/categories') setCurrentScreen('categories');
      else if (path === '/calendar') setCurrentScreen('calendar');
      else if (path === '/store') setCurrentScreen('store');
      else if (path === '/login') setCurrentScreen('login');
      else if (path === '/otp') setCurrentScreen('otp');
      else setCurrentScreen('home');
    }
  }, [showSplash, hasSeenOnboarding]);

  useEffect(() => {
    // Simulate splash screen duration
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  const handleOnboardingComplete = () => {
    setHasSeenOnboarding(true);
    localStorage.setItem('hasSeenOnboarding', 'true');
    setCurrentScreen('home');
  };

  const handlePrototypeNavigate = (screen: string, path?: string) => {
    setCurrentScreen(screen);
    
    if (screen === 'splash') {
      setShowSplash(true);
      setHasSeenOnboarding(false);
      localStorage.removeItem('hasSeenOnboarding');
    } else if (screen === 'onboarding') {
      setShowSplash(false);
      setHasSeenOnboarding(false);
      localStorage.setItem('hasSeenOnboarding', 'false');
    } else {
      setShowSplash(false);
      setHasSeenOnboarding(true);
      localStorage.setItem('hasSeenOnboarding', 'true');
      
      // Navigate to the page - comprehensive path mapping
      const paths: { [key: string]: string } = {
        'home': '/',
        'categories': '/categories',
        'calendar': '/calendar',
        'store': '/store',
        'login': '/login',
        'otp': '/otp',
        'profile': '/profile',
        'support': '/support',
        'forgot-password': '/forgot-password',
        'delete-account': '/delete-account',
        'course-details': '/course/1',
        'chapters-list': '/course/1/chapters',
        'chapter-details': '/course/1/chapter/1',
        'video-player': '/course/1/chapter/1/video/1',
        'quiz-attempt': '/course/1/chapter/1/quiz/1',
        'quiz-results': '/course/1/chapter/1/quiz/1/results',
        'live-sessions-list': '/live-sessions',
        'live-session-details': '/live-session/1',
        'live-session-room': '/live-session/1/room',
        'online-course-details': '/online-course/1',
        'session-details': '/online-course/1/session/1',
        'payment-proof': '/payment/1/proof',
        'payment-status': '/payment/1/status',
        'edit-profile': '/profile/edit',
        'my-courses': '/profile/courses',
        'quizzes': '/profile/quizzes',
        'my-books': '/profile/books',
        'orders': '/orders',
        'about': '/profile/about',
        'privacy': '/profile/privacy',
        'settings': '/settings',
        'all-courses': '/courses/all',
        'all-resources': '/resources/all',
        'all-live-sessions': '/live-sessions/all'
      };
      
      // Use path parameter if provided, otherwise use mapping
      const targetPath = path || paths[screen];
      if (targetPath && window.location.pathname !== targetPath) {
        window.location.href = targetPath;
      }
    }
  };

  return (
    <>
      {showSplash || !hasSeenOnboarding ? (
        <>
          <PrototypeNav currentScreen={currentScreen} onNavigate={handlePrototypeNavigate} />
          {showSplash ? (
            <SplashScreen />
          ) : (
            <Onboarding onComplete={handleOnboardingComplete} />
          )}
        </>
      ) : (
        <Router>
          <PrototypeNavWrapper currentScreen={currentScreen} onNavigate={handlePrototypeNavigate} />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/store" element={<Store />} />
            <Route path="/login" element={<Login />} />
            <Route path="/otp" element={<OTP />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/profile/edit" element={<EditProfile />} />
            <Route path="/profile/courses" element={<MyCourses />} />
            <Route path="/profile/quizzes" element={<Quizzes />} />
            <Route path="/profile/books" element={<MyBooks />} />
            <Route path="/profile/about" element={<About />} />
            <Route path="/profile/privacy" element={<Privacy />} />
            <Route path="/course/:id" element={<CourseDetails />} />
            <Route path="/course/:id/chapters" element={<ChaptersList />} />
            <Route path="/course/:id/chapter/:chapterId" element={<ChapterDetails />} />
            <Route path="/course/:id/chapter/:chapterId/video/:videoId" element={<VideoPlayer />} />
            <Route path="/course/:id/chapter/:chapterId/quiz/:quizId" element={<QuizAttempt />} />
            <Route path="/course/:id/chapter/:chapterId/quiz/:quizId/results" element={<QuizResults />} />
            <Route path="/live-sessions" element={<LiveSessionsList />} />
            <Route path="/live-session/:id" element={<LiveSessionDetails />} />
            <Route path="/live-session/:id/room" element={<LiveSessionRoom />} />
            <Route path="/online-course/:id" element={<OnlineCourseDetails />} />
            <Route path="/online-course/:id/chapter/:chapterId" element={<OnlineChapterDetails />} />
            <Route path="/online-course/:id/session/:sessionId" element={<LiveSessionDetails />} />
            <Route path="/online-course/:id/session/:sessionId/room" element={<LiveSessionRoom />} />
            <Route path="/online-course/:id/chapter/:chapterId/quiz/:quizId" element={<QuizAttempt />} />
            <Route path="/payment/:id/proof" element={<PaymentProof />} />
            <Route path="/payment/:id/status" element={<PaymentStatus />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/support" element={<Support />} />
            <Route path="/courses/all" element={<AllCourses />} />
            <Route path="/resources/all" element={<AllResources />} />
            <Route path="/resource/:id" element={<ResourceDetails />} />
            <Route path="/live-sessions/all" element={<AllLiveSessions />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      )}
    </>
  );
}

export default App;

